"use client";

import { useEffect, useRef } from "react";
import { Button } from "@/scott/ui/elements/button/button";
import type { ProductDetailLocallyParams } from "../product-detail-page.adapter";

declare global {
  interface Window {
    lcly_config_0?: Record<string, string>;
    lclyInlineModalGlobal?: (
      mode: string,
      params: string,
      options?: unknown,
      widgetId?: unknown,
      steps?: string[]
    ) => Promise<void>;
  }
}

const LOCALLY_COMPANY_ID = process.env.NEXT_PUBLIC_LOCALLY_COMPANY_ID ?? "";
const LOCALLY_EMBED_URL = process.env.NEXT_PUBLIC_LOCALLY_EMBED_URL ?? "";

const SDK_POLL_INTERVAL_MS = 100;
const SDK_POLL_TIMEOUT_MS = 5000;
const LEGACY_TRIGGER_TIMEOUT_MS = 10_000;

interface SdkLoad {
  ready: boolean;
  whenReady: Promise<void>;
}

/**
 * Load state, keyed by the script element. The script outlives the component:
 * after a client-side navigation the next PDP finds the tag still loading, so
 * it must wait for the same load instead of taking the tag as proof the SDK is
 * ready.
 */
const sdkLoads = new WeakMap<Element, SdkLoad>();

/**
 * The SDK sets window.lclyInlineModalGlobal inside a React useEffect, so it may
 * not be available when the script fires "load". Poll briefly. On timeout the
 * open falls back to the legacy trigger.
 */
function waitForSdkGlobal(done: () => void) {
  const start = Date.now();
  const poll = setInterval(() => {
    if (
      window.lclyInlineModalGlobal ||
      Date.now() - start > SDK_POLL_TIMEOUT_MS
    ) {
      clearInterval(poll);
      done();
    }
  }, SDK_POLL_INTERVAL_MS);
}

function trackSdkLoad(
  script: Element,
  start: (settle: () => void) => void
): SdkLoad {
  const load: SdkLoad = { ready: false, whenReady: Promise.resolve() };
  load.whenReady = new Promise((resolve) => {
    start(() => {
      load.ready = true;
      resolve();
    });
  });
  sdkLoads.set(script, load);
  return load;
}

function loadLocallySdk(): SdkLoad | undefined {
  if (!LOCALLY_EMBED_URL) {
    return;
  }

  // The SDK (inline_modal v1.4+) renders a React app into #__lcly_inline_modal.
  // Create that container before loading the script so createRoot() finds it.
  if (!document.getElementById("__lcly_inline_modal")) {
    const container = document.createElement("div");
    container.id = "__lcly_inline_modal";
    document.body.appendChild(container);
  }

  const existing = document.getElementById("lcly-embed");
  if (existing) {
    // An untracked tag was added outside this module (e.g. before a hot
    // reload). Its load event may already have fired, so only wait for the
    // global.
    return sdkLoads.get(existing) ?? trackSdkLoad(existing, waitForSdkGlobal);
  }

  const script = document.createElement("script");
  script.id = "lcly-embed";
  script.src = LOCALLY_EMBED_URL;
  script.async = true;
  const load = trackSdkLoad(script, (settle) => {
    script.addEventListener("load", () => waitForSdkGlobal(settle), {
      once: true,
    });
    script.addEventListener(
      "error",
      () => {
        // Let the next intent retry.
        script.remove();
        settle();
      },
      { once: true }
    );
  });
  document.head.appendChild(script);
  return load;
}

interface ProductStoreFinderLocallyProps {
  locallyParams: ProductDetailLocallyParams;
  buttonLabel: string;
  buttonSubtitle?: string;
}

/**
 * The SDK is about 270 KB of compressed JavaScript, and most shoppers never
 * open the store finder. Load it on the first sign of intent (pointer over
 * the button, keyboard focus, touch) instead of on every page view.
 */
function preloadSdk() {
  loadLocallySdk();
}

export function ProductStoreFinderLocally({
  locallyParams,
  buttonLabel,
  buttonSubtitle,
}: Readonly<ProductStoreFinderLocallyProps>) {
  const paramsRef = useRef(locallyParams);
  const pendingOpen = useRef(false);

  useEffect(() => {
    paramsRef.current = locallyParams;
    window.lcly_config_0 = {
      company_id: LOCALLY_COMPANY_ID,
      upc: locallyParams.ean,
      product_id: locallyParams.productId,
      ...(locallyParams.category ? { category: locallyParams.category } : {}),
      lang: locallyParams.lang,
    };
  }, [locallyParams]);

  // A click may still be waiting for the SDK. Drop it if the shopper leaves
  // the page first, so the finder never opens later on another page.
  useEffect(
    () => () => {
      pendingOpen.current = false;
    },
    []
  );

  function openLocally() {
    if (typeof window.lclyInlineModalGlobal === "function") {
      const locallyParams = paramsRef.current;
      const params = new URLSearchParams({
        company_id: LOCALLY_COMPANY_ID,
        upc: locallyParams.ean ?? "",
        product_id: locallyParams.productId ?? "",
        ...(locallyParams.category ? { category: locallyParams.category } : {}),
        lang: locallyParams.lang ?? "",
      });
      // Mode "sfl" (store-finder/locator): when no prior store is selected the SDK
      // automatically switches to "fallback_sfl", navigating straight to the map view
      // without calling the location_data endpoint (which crashes with store_id=null).
      //
      // company_id in options prevents the SDK from overwriting it with null
      // (u.company_id = options?.company_id ?? null in the SDK internals).
      //
      // Skip GET_CART_DETAIL: calls /headless/api/1.0/cart which requires a merchant-level
      // cart subscription that company 1543 does not have (error_code 1206).
      window
        .lclyInlineModalGlobal(
          "sfl",
          params.toString(),
          { company_id: LOCALLY_COMPANY_ID },
          undefined,
          [
            "ENABLE_FORCED_APP_LOADING_STATE",
            "GET_SESSION",
            "DISABLE_FORCED_APP_LOADING_STATE",
            "GET_COMMON",
          ]
        )
        .catch(() => {});
      return;
    }

    // Legacy fallback: trigger the button injected by the older SDK version.
    const container = document.getElementById("lcly-button-0");
    if (!container) {
      return;
    }

    const trigger = container.querySelector<HTMLElement>(
      ".lcly-primary-trigger"
    );
    if (trigger) {
      trigger.click();
      return;
    }

    const observer = new MutationObserver(() => {
      const t = container.querySelector<HTMLElement>(".lcly-primary-trigger");
      if (t) {
        observer.disconnect();
        t.click();
      }
    });
    observer.observe(container, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), LEGACY_TRIGGER_TIMEOUT_MS);
  }

  /** A click that lands before the SDK is ready opens the finder once it is. */
  function handleClick() {
    const sdk = loadLocallySdk();
    if (!sdk) {
      return;
    }
    if (sdk.ready) {
      openLocally();
      return;
    }
    pendingOpen.current = true;
    void sdk.whenReady.then(() => {
      if (pendingOpen.current) {
        pendingOpen.current = false;
        openLocally();
      }
    });
  }

  return (
    <>
      <Button
        className="h-[71px] w-full flex-col items-start justify-between rounded border border-alabastergrey p-3"
        onClick={handleClick}
        onFocus={preloadSdk}
        onPointerEnter={preloadSdk}
        onTouchStart={preloadSdk}
        type="button"
        variant="outline"
      >
        <span className="font-bold font-brand text-[12px] text-black leading-[14px] tracking-[0.02em]">
          {buttonLabel}
        </span>
        {buttonSubtitle && (
          <span className="font-brand font-normal text-[12px] text-black leading-[17px] tracking-normal">
            {buttonSubtitle}
          </span>
        )}
      </Button>
      {/* Legacy container kept for older SDK fallback */}
      <div aria-hidden="true" className="hidden" id="lcly-button-0" />
    </>
  );
}
